import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type BackgroundType = 'squares' | 'squares-orange' | 'squares-blue' | 'squares-green' | 'shader-sunset' | 'shader-ocean' | 'shader-forest' | 'shader-cosmic' | 'shader-aurora' | 'geometric-hexagon' | 'geometric-triangle' | 'geometric-diamond' | 'geometric-wave' | 'gradient-warm' | 'gradient-cool' | 'gradient-neon' | 'gradient-earth' | 'particle-flow' | 'particle-spark' | 'particle-galaxy' | 'particle-neural' | 'video-background' | 'video-abstract-particles' | 'video-ocean-waves' | 'video-forest-mist' | 'video-digital-matrix' | 'video-cosmic-nebula' | 'video-flowing-water' | 'video-night-city' | 'video-golden-sunset' | 'video-aurora-lights' | 'video-fire-flames';

export type LockScreenBackground = 'video-background' | 'video-abstract-particles' | 'video-ocean-waves' | 'video-forest-mist' | 'video-digital-matrix' | 'video-cosmic-nebula' | 'video-flowing-water' | 'video-night-city' | 'video-golden-sunset' | 'video-aurora-lights' | 'video-fire-flames';

export type TimeMode = 'auto' | 'day' | 'night' | 'rainbow';

export type DockTheme = 'default' | 'ocean-blue' | 'midnight' | 'custom';

export type IconStyle = 'filled' | 'outlined' | 'minimal';

export type CascadeOffset = 8 | 12 | 16;

interface DockCustomization {
  theme: DockTheme;
  customColor?: string;
  iconStyle: IconStyle;
}

interface WindowCascadeSettings {
  enabled: boolean;
  offset: CascadeOffset;
}

interface PreferencesContextType {
  // Background settings
  backgroundType: BackgroundType;
  setBackgroundType: (type: BackgroundType) => void;
  lockScreenBackground: LockScreenBackground;
  setLockScreenBackground: (type: LockScreenBackground) => void;
  backgroundOverlayOpacity: number;
  setBackgroundOverlayOpacity: (opacity: number) => void;
  frostedGlassMode: boolean;
  setFrostedGlassMode: (enabled: boolean) => void;
  
  // Display settings
  timeMode: TimeMode;
  setTimeMode: (mode: TimeMode) => void;
  
  // Dock customization
  dockCustomization: DockCustomization;
  setDockCustomization: (customization: Partial<DockCustomization>) => void;
  
  // Window cascade settings
  windowCascade: WindowCascadeSettings;
  setWindowCascade: (settings: Partial<WindowCascadeSettings>) => void;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

interface PreferencesProviderProps {
  children: ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({ children }) => {
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('current_organization_id');
    } catch {
      return null;
    }
  });
  
  const [backgroundType, setBackgroundTypeState] = useState<BackgroundType>('squares');
  const [lockScreenBackground, setLockScreenBackgroundState] = useState<LockScreenBackground>('video-background');
  const [backgroundOverlayOpacity, setBackgroundOverlayOpacityState] = useState<number>(25);
  const [frostedGlassMode, setFrostedGlassModeState] = useState<boolean>(false);
  const [timeMode, setTimeModeState] = useState<TimeMode>('auto');
  const [dockCustomization, setDockCustomizationState] = useState<DockCustomization>({
    theme: 'default',
    iconStyle: 'filled'
  });
  const [windowCascade, setWindowCascadeState] = useState<WindowCascadeSettings>({
    enabled: true,
    offset: 12
  });
  
  // Helper to get org-specific localStorage key
  const getOrgKey = (key: string, orgId: string | null = null): string => {
    const activeOrgId = orgId || localStorage.getItem('current_organization_id');
    if (activeOrgId) {
      return `org_${activeOrgId}_${key}`;
    }
    return key; // Fallback to global key if no org
  };

  // Function to load preferences from localStorage for a specific org
  const loadPreferences = (orgId: string | null = null) => {
    const activeOrgId = orgId || localStorage.getItem('current_organization_id');
    const orgName = localStorage.getItem('current_organization_name');
    console.log('[Preferences] Loading preferences for org:', activeOrgId, `(${orgName || 'null'})`);
    
    const savedBackground = localStorage.getItem(getOrgKey('dashboard-background', activeOrgId));
    const savedLockScreen = localStorage.getItem(getOrgKey('lockscreen-background', activeOrgId));
    const savedOverlayOpacity = localStorage.getItem(getOrgKey('background-overlay-opacity', activeOrgId));
    const savedFrostedGlass = localStorage.getItem(getOrgKey('frosted-glass-mode', activeOrgId));
    const savedTimeMode = localStorage.getItem(getOrgKey('time-mode', activeOrgId));
    const savedDockCustomization = localStorage.getItem(getOrgKey('dock-customization', activeOrgId));
    const savedWindowCascade = localStorage.getItem(getOrgKey('window-cascade', activeOrgId));
    
    console.log('[Preferences] 📥 LOADING preferences:', {
      orgId: activeOrgId,
      key: getOrgKey('dashboard-background', activeOrgId),
      savedBackground,
      willSetTo: savedBackground || 'squares (default)'
    });

    if (savedBackground && Object.values(['squares', 'squares-orange', 'squares-blue', 'squares-green', 'shader-sunset', 'shader-ocean', 'shader-forest', 'shader-cosmic', 'shader-aurora', 'geometric-hexagon', 'geometric-triangle', 'geometric-diamond', 'geometric-wave', 'gradient-warm', 'gradient-cool', 'gradient-neon', 'gradient-earth', 'particle-flow', 'particle-spark', 'particle-galaxy', 'particle-neural', 'video-background', 'video-abstract-particles', 'video-ocean-waves', 'video-forest-mist', 'video-digital-matrix', 'video-cosmic-nebula', 'video-flowing-water', 'video-night-city', 'video-golden-sunset', 'video-aurora-lights', 'video-fire-flames']).includes(savedBackground as BackgroundType)) {
      setBackgroundTypeState(savedBackground as BackgroundType);
    } else {
      setBackgroundTypeState('squares'); // Reset to default if no saved preference
    }

    if (savedLockScreen && ['video-background', 'video-abstract-particles', 'video-ocean-waves', 'video-forest-mist', 'video-digital-matrix', 'video-cosmic-nebula', 'video-flowing-water', 'video-night-city', 'video-golden-sunset', 'video-aurora-lights', 'video-fire-flames'].includes(savedLockScreen)) {
      setLockScreenBackgroundState(savedLockScreen as LockScreenBackground);
    } else {
      setLockScreenBackgroundState('video-background');
    }

    if (savedOverlayOpacity) {
      const opacity = parseInt(savedOverlayOpacity, 10);
      if (!isNaN(opacity) && opacity >= 0 && opacity <= 100) {
        setBackgroundOverlayOpacityState(opacity);
      }
    } else {
      setBackgroundOverlayOpacityState(25);
    }

    if (savedFrostedGlass) {
      setFrostedGlassModeState(savedFrostedGlass === 'true');
    } else {
      setFrostedGlassModeState(false);
    }

    if (savedTimeMode && ['auto', 'day', 'night', 'rainbow'].includes(savedTimeMode)) {
      setTimeModeState(savedTimeMode as TimeMode);
    } else {
      setTimeModeState('auto');
    }

    if (savedDockCustomization) {
      try {
        const parsed = JSON.parse(savedDockCustomization);
        setDockCustomizationState(parsed);
      } catch (e) {
        console.error('Failed to parse dock customization:', e);
        setDockCustomizationState({ theme: 'default', iconStyle: 'filled' });
      }
    } else {
      setDockCustomizationState({ theme: 'default', iconStyle: 'filled' });
    }
    
    if (savedWindowCascade) {
      try {
        const parsed = JSON.parse(savedWindowCascade);
        setWindowCascadeState(parsed);
      } catch (e) {
        console.error('Failed to parse window cascade settings:', e);
        setWindowCascadeState({ enabled: true, offset: 12 });
      }
    } else {
      setWindowCascadeState({ enabled: true, offset: 12 });
    }
  };

  // Load preferences on mount and when organization changes
  useEffect(() => {
    // Check if organization ID changed
    const checkOrgChange = () => {
      const newOrgId = localStorage.getItem('current_organization_id');
      if (newOrgId !== currentOrgId) {
        const newOrgName = localStorage.getItem('current_organization_name');
        console.log('[Preferences] 🔄 ORG SWITCH DETECTED:', {
          from: currentOrgId,
          to: newOrgId,
          toName: newOrgName
        });
        setCurrentOrgId(newOrgId);
        // Load preferences for the NEW org immediately
        console.log('[Preferences] 📂 Loading preferences for NEW org:', newOrgId);
        loadPreferences(newOrgId);
      }
    };
    
    // Initial load with current org
    loadPreferences(currentOrgId);
    
    // Check for org changes periodically (same pattern as AppsManager)
    const interval = setInterval(checkOrgChange, 1000);
    
    // Listen for preference updates from other components
    const handlePreferenceUpdate = (event: CustomEvent) => {
      const { key, value } = event.detail;
      if (key === 'lockscreen-background') {
        setLockScreenBackgroundState(value);
      }
    };

    window.addEventListener('preferences:updated', handlePreferenceUpdate as EventListener);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('preferences:updated', handlePreferenceUpdate as EventListener);
    };
  }, [currentOrgId]);

  // Save preferences to localStorage when changed (with org prefix)
  const setBackgroundType = (type: BackgroundType) => {
    const currentOrgId = localStorage.getItem('current_organization_id');
    const key = getOrgKey('dashboard-background');
    console.log('[Preferences] 🎨 SAVING background:', {
      type,
      orgId: currentOrgId,
      key,
      stateOrgId: currentOrgId
    });
    setBackgroundTypeState(type);
    localStorage.setItem(key, type);
    // Verify it was saved
    const verified = localStorage.getItem(key);
    console.log('[Preferences] ✅ VERIFIED save:', key, '=', verified);
  };

  const setLockScreenBackground = (type: LockScreenBackground) => {
    setLockScreenBackgroundState(type);
    localStorage.setItem(getOrgKey('lockscreen-background'), type);
    console.log('[Preferences] Saved lockscreen:', type, 'with key:', getOrgKey('lockscreen-background'));
    // Dispatch event for cross-component sync
    window.dispatchEvent(new CustomEvent('preferences:updated', { 
      detail: { key: 'lockscreen-background', value: type } 
    }));
  };

  const setTimeMode = (mode: TimeMode) => {
    setTimeModeState(mode);
    localStorage.setItem(getOrgKey('time-mode'), mode);
    console.log('[Preferences] Saved time mode:', mode, 'with key:', getOrgKey('time-mode'));
  };

  const setBackgroundOverlayOpacity = (opacity: number) => {
    setBackgroundOverlayOpacityState(opacity);
    localStorage.setItem(getOrgKey('background-overlay-opacity'), opacity.toString());
  };

  const setFrostedGlassMode = (enabled: boolean) => {
    setFrostedGlassModeState(enabled);
    localStorage.setItem(getOrgKey('frosted-glass-mode'), enabled.toString());
  };

  const setDockCustomization = (customization: Partial<DockCustomization>) => {
    const updated = { ...dockCustomization, ...customization };
    setDockCustomizationState(updated);
    localStorage.setItem(getOrgKey('dock-customization'), JSON.stringify(updated));
  };
  
  const setWindowCascade = (settings: Partial<WindowCascadeSettings>) => {
    const updated = { ...windowCascade, ...settings };
    setWindowCascadeState(updated);
    localStorage.setItem(getOrgKey('window-cascade'), JSON.stringify(updated));
    console.log('[Preferences] Saved window cascade:', updated, 'with key:', getOrgKey('window-cascade'));
  };

  return (
    <PreferencesContext.Provider value={{ 
      backgroundType, 
      setBackgroundType,
      lockScreenBackground,
      setLockScreenBackground,
      backgroundOverlayOpacity,
      setBackgroundOverlayOpacity,
      frostedGlassMode,
      setFrostedGlassMode,
      timeMode,
      setTimeMode,
      dockCustomization,
      setDockCustomization,
      windowCascade,
      setWindowCascade
    }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};